import React, { useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux';
import { classNames } from '../../utils/classNames';
import { useSignInWithAgentMutation } from '../../store/auth/authApi';
import { useSignUpWithAgentMutation } from '../../store/user/userApi';
import { useLocation } from 'react-router-dom';
import { RootState } from '../../app/store';

type SpeechRecognition = any
type SpeechRecognitionEvent = any

const AuthChatInput = ({
	type = 'sign-up'
}: {
	type?: 'sign-up' | 'sign-in'
}) => {
	const [chatId, setChatId] = useState<string | null>(null);
	const [isListening, setIsListening] = useState(false);
	const recognitionRef = useRef<SpeechRecognition | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const { search } = useLocation();
	const queryParams = new URLSearchParams(search);
	const username = queryParams.get('username');

	const [signUpAgent, { isLoading: isSignUpLoading }] = useSignUpWithAgentMutation()
	const [signInAgent, { isLoading: isSignInLoading }] = useSignInWithAgentMutation()
	const { register, handleSubmit, reset, setValue } = useForm<{ message: string }>();
	const isBusy = isSignInLoading || isSignUpLoading;

	const { authMessages } = useSelector(
		(state: RootState) => state.chats
	);

	useEffect(() => {
		const SpeechRecognition =
			(window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

		if (SpeechRecognition) {
			const recognition = new SpeechRecognition();
			recognition.lang = 'en-US';
			recognition.interimResults = false;
			recognition.maxAlternatives = 1;

			recognition.onresult = (event: SpeechRecognitionEvent) => {
				const transcript = event.results[0][0].transcript;
				if (inputRef.current) {
					inputRef.current.value = transcript;
				}
				setValue('message', transcript);
			};

			recognition.onend = () => {
				setIsListening(false);
			};

			recognition.onerror = (err: any) => {
				console.error('Speech recognition error', err);
				setIsListening(false);
			};

			recognitionRef.current = recognition;
		} else {
			console.error('Speech recognition not supported in this browser');
		}
	}, []);

	const onSubmit = async ({ message }: { message: string }) => {
		if (isBusy || (!message || message === '')) return;
		try {
			reset({ message: '' })
			const { stage, sort, flow } = authMessages[authMessages.length - 1]?.agent || {};
			const payload = {
				...(chatId ? { chatId } : {}),
				dashboard: 'Claire',
				chatData: [
					...authMessages,
					{
						user: {
							message,
							stage,
							sort: sort + 1,
							flow
						}
					}
				]
			};
			if (type === 'sign-up') {
				const { data } = await signUpAgent(payload).unwrap();
				setChatId(data.metadata.chatId || null);
			} else {
				const { data } = await signInAgent(payload).unwrap();
				setChatId(data.data.metadata.chatId || null);
			}
		} catch (error) {
			const err = error as { name: string }
			if (err?.name === 'AbortError') {
				// console.log('Request was cancelled.');
			} else {
				// console.log({ err })
			}
		} finally {
			// setTimeout(() => {
			//     contentEditableRef.current?.focus();
			// }, 100);
		}
	}

	useEffect(() => {
		if (username) {
			onSubmit({ message: username })
		}
	}, [username])

	const handleMicClick = () => {
		if (isListening || !recognitionRef.current) return;
		setIsListening(true);
		recognitionRef.current.start();
	};

	const handleKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
		if (evt.key === 'Enter' && !evt.shiftKey) {
			evt.preventDefault();
			handleSubmit(onSubmit)();
		}
	};
	return (
		<form className='flex flex-wrap max-w-full xl:w-[760px] relative mx-auto' onSubmit={handleSubmit(onSubmit)}>
			<div className='relative w-full'>
				<button
					type="button"
					onClick={handleMicClick}
					disabled={isListening}
					className={classNames(
						'absolute left-3 top-1/2 transform -translate-y-1/2 w-7 h-7',
						isListening ? 'animate-pulse' : ''
					)}
				>
					<img src='/svg/icons/microphone.svg' alt='Mic' />
				</button>
				<input
					{...register('message')}
					autoComplete='off'
					onKeyDown={handleKeyDown}
					disabled={isBusy}
					placeholder='Ask something...'
					className={`border-2 border-primary-default rounded-2xl py-3 px-4 pl-10 outline-none w-full text-sm pr-10` + (isBusy ? ' opacity-50' : '')}
					type="text"
				/>
				<button className='absolute right-3 top-2.5' disabled={isSignInLoading || isSignUpLoading} type='submit'>
					<img src='/svg/icons/send.svg' />
				</button>
			</div>
		</form>
	)
}

export default AuthChatInput